import { ReactNode } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { RootState } from "@/store";

interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  roles?: string[];
}

/**
 * AuthGuard component for protecting routes based on authentication state
 *
 * @param children - The components to render if authentication check passes
 * @param requireAuth - Whether authentication is required (default: true)
 * @param redirectTo - Where to redirect if auth check fails (default: '/login' or '/dashboard')
 * @param roles - Optional array of roles that are allowed to access the route
 */
export const AuthGuard = ({
  children,
  requireAuth = true,
  redirectTo,
  roles,
}: AuthGuardProps) => {
  const { isAuthenticated, member } = useSelector(
    (state: RootState) => state.auth
  );
  const location = useLocation();

  // Determine where to redirect based on auth state
  const getRedirectPath = () => {
    if (requireAuth && !isAuthenticated) {
      // User needs to be logged in but isn't
      return redirectTo || "/signup";
    } else if (!requireAuth && isAuthenticated) {
      // User is logged in but accessing a public-only route
      return redirectTo || "/dashboard";
    }
    return null;
  };

  // Check if user has required role
  const hasRequiredRole = () => {
    if (!roles || roles.length === 0) return true;
    if (!member || !member.roleType) return false;
    return roles.includes(member.roleType);
  };

  // Get redirect path based on auth state and role requirements
  const redirectPath = getRedirectPath();

  // If role check fails, redirect to dashboard
  if (isAuthenticated && !hasRequiredRole()) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  // If auth check fails, redirect to specified path
  if (redirectPath) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // Auth check passed, render children
  return <>{children}</>;
};
